import type { ReactNode } from 'react'
import styled from 'styled-components'
import { thinScrollbarCss } from 'styles/globalCss'
import { theme } from 'styles/theme'
import { GhostButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'
import { withCustomTooltips } from '../Tooltip/TooltipManager'
import { UPDATE_REQUEST_ROW_HEIGHT } from './UpdateRequestRow'

const MAX_SHOWN_ROWS = 6

const Window = styled.section`
    position: fixed;
    inset-inline-end: 1.5rem;
    inset-block-end: 1.5rem;
    /* Above the pages, below the snackbar notifications (100) and the dialogs (300) */
    z-index: 99;
    inline-size: 26rem;
    max-inline-size: calc(100vw - 3rem);
    border-radius: ${theme.radii.medium};
    background-color: ${theme.colors.LightBackground};
    box-shadow: 0 2px 6px 2px rgb(0 0 0 / 15%);
`

const Header = styled.header`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 15px 24px 0;
`

const Titles = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-inline-size: 0;
`

const Title = styled.h2`
    margin: 0;
    font-size: calc(${theme.fontSizes.medium} + 1px);
    font-weight: ${theme.fontWeights.bold};
    line-height: 24px;
    letter-spacing: 0.02em;
    color: ${theme.colors.MainBlack};
`

const SubTitle = styled.span`
    font-size: calc(${theme.fontSizes.small} + 1px);
    line-height: 15px;
    color: ${theme.colors.SecondaryGrey};
`

// The message of all the requests, when they have the same one
const Message = styled.p`
    margin: 0.75rem 0 0;
    font-size: calc(${theme.fontSizes.tiny} + 1px);
    color: ${theme.colors.Red};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const DismissAllButton = styled(GhostButton)`
    --size: 16px;
    --block-size: 16px;
`

const Rows = styled.ul`
    margin: 0;
    padding: 15px 24px;
    list-style: none;
    max-block-size: calc(${MAX_SHOWN_ROWS} * ${UPDATE_REQUEST_ROW_HEIGHT} + 30px);
    overflow-y: auto;
    ${thinScrollbarCss}
`

interface UpdateRequestsViewerProps {
    subTitle: string
    message?: string
    // Without it, the requests can't be dismissed all at once
    dismissAll?: { isDismissing: boolean, onDismissAll: VoidFunction }
    // The UpdateRequestRows
    children: ReactNode
}

// A floating window with the profile update requests of the center's vehicles
export const UpdateRequestsViewer = ({ subTitle, message, dismissAll, children }: UpdateRequestsViewerProps) =>
    <Window {...{ 'aria-label': 'Version profile updates', ...withCustomTooltips }}>
        <Header>
            <Titles>
                <Title>Version profile update</Title>
                <SubTitle>{ subTitle }</SubTitle>
                { message && <Message {...{ title: message }}>{ message }</Message> }
            </Titles>
            { dismissAll &&
                <DismissAllButton {...{ text: 'Dismiss all', 'aria-label': 'Dismiss all', isDisabled: dismissAll.isDismissing, onClick: dismissAll.onDismissAll }}>
                    <SvgIcons.CloseXSvg />
                </DismissAllButton>
            }
        </Header>
        <Rows>{ children }</Rows>
    </Window>
